import { HttpErrorResponse } from '@angular/common/http';

import { UserSelectors } from '@recruitee/account-data';
import { CandidateSelectors } from '@recruitee/candidate-data';
import type { ShareOnTheGoRequestPayload } from '@recruitee/candidate-types';
import type {
  InterviewResultRequest,
  InterviewResultRequestMeta,
} from '@recruitee/interview-result-request-types';
import { InterviewTemplateSelectors } from '@recruitee/interview-templates-data';
import {
  CorrelationId,
  createEntityCollectionStore,
  EntityState,
  getEntityCollectionActions,
  ngrxEvent,
  objectToScope,
  pipeState,
} from '@recruitee/ngrx';
import { JobsBriefSelectors, StageSelectors } from '@recruitee/offers-data';
import { NewTalentPoolsBriefSelectors } from '@recruitee/talent-pools-data';

import { CreateParams, FetchParams } from './interview-result-request-http-client';

type State = EntityState<InterviewResultRequest, { scopeMeta: InterviewResultRequestMeta }>;

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } = createEntityCollectionStore<
  InterviewResultRequest,
  { scopeMeta: InterviewResultRequestMeta }
>()('interview-result-request', {
  idSelector,
  references: makeRef => [
    makeRef('adminId', 'admin', UserSelectors.byId),
    makeRef('offerId', 'offer', id => JobsBriefSelectors.byId(id!)),
    makeRef('talentPoolId', 'talentPool', id => NewTalentPoolsBriefSelectors.byId(id!)),
    makeRef('stageId', 'stage', StageSelectors.byId),
    makeRef('candidateId', 'candidate', CandidateSelectors.byId),
    makeRef('interviewTemplateId', 'interviewTemplate', InterviewTemplateSelectors.byId),
  ],
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const actions = getEntityCollectionActions(adapter, idSelector);

    const fetchCollection = (state: State, payload: { params: { scope: string } & FetchParams }) =>
      adapter.markAsPending(state, { scope: objectToScope(payload.params), entities: [] });
    const fetchCollectionSuccess = (
      state: State,
      payload: { data: InterviewResultRequest[]; meta: InterviewResultRequestMeta; scope: string },
    ) =>
      pipeState(
        state,
        next => adapter.resetScope(next, payload.scope),
        next => adapter.setMany(next, payload.data, { scope: payload.scope }),
        next => adapter.setScopeMetaCustom(next, payload.meta, { scope: payload.scope }),
        next => adapter.markAsLoaded(next, { scope: payload.scope, entities: [] }),
      );

    return {
      createOnePessimistic: (
        state,
        payload: {
          candidateId: number;
          data: CreateParams[];
          shareOnTheGoRequestPayload?: ShareOnTheGoRequestPayload;
        } & Required<CorrelationId>,
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),

      createOnePessimisticSuccess: ngrxEvent<{
        data: InterviewResultRequest;
        correlationId: string;
        grantAccess?: ShareOnTheGoRequestPayload['grantAccess'];
      }>(),

      fetchCollection,
      fetchCollectionSuccess: fetchCollectionSuccess,

      fetchCollectionBuffered: fetchCollection,
      fetchCollectionBufferedSuccess: fetchCollectionSuccess,
      fetchCollectionBufferedFailed: actions.fetchCollectionFailed,

      dismiss: (state, payload: { id: number }) =>
        adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
      dismissSuccess: (state, payload: { data: InterviewResultRequest }) =>
        pipeState(
          state,
          next => adapter.setOne(next, payload.data),
          next => adapter.markAsLoaded(next, { scope: null, entities: [payload.data.id] }),
        ),
      dismissFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
        adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

      dismissAll: (state, payload: { ids: number[] }) => state,
      dismissAllSuccess: (
        state,
        payload: { data: InterviewResultRequest[]; meta: InterviewResultRequestMeta },
      ) => state,
      dismissAllFailed: (state, payload) => state,

      revert: (state, payload: { id: number }) =>
        adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
      revertSuccess: (state, payload: { data: InterviewResultRequest }) =>
        pipeState(
          state,
          next => adapter.setOne(next, payload.data),
          next => adapter.markAsLoaded(next, { scope: null, entities: [payload.data.id] }),
        ),
      revertFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
        adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    };
  },
});

const InterviewResultRequestActions = <
  Pick<
    typeof actions,
    | 'fetchCollection'
    | 'fetchCollectionSuccess'
    | 'fetchCollectionFailed'
    | 'createOnePessimistic'
    | 'createOnePessimisticSuccess'
    | 'createOnePessimisticFailed'
    | 'removeOnePessimistic'
    | 'removeOnePessimisticSuccess'
    | 'removeOnePessimisticFailed'
    | 'fetchCollectionBuffered'
    | 'fetchCollectionBufferedSuccess'
    | 'fetchCollectionBufferedFailed'
    | 'dismiss'
    | 'dismissSuccess'
    | 'dismissFailed'
    | 'dismissAll'
    | 'dismissAllSuccess'
    | 'dismissAllFailed'
    | 'revert'
    | 'revertSuccess'
    | 'revertFailed'
  >
>actions;

export {
  InterviewResultRequestActions,
  selectors as InterviewResultRequestSelectors,
  namespace as InterviewResultRequestNamespace,
  reducer as InterviewResultRequestReducer,
};
